import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { NotificationBell } from './NotificationBell';
import { SearchModal } from '../search/SearchModal';
import { useSearch } from '../../hooks/useSearch';
import { useTheme } from '../../context/ThemeContext';
import { updateThemePreference } from '../../api/auth';
import {
  getNotificationPermission,
  requestNotificationPermission,
  getNotificationPrivacyMode,
  setNotificationPrivacyMode,
  getNotificationsEnabled,
  setNotificationsEnabled
} from '../../utils/osNotifications';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isOpen: isSearchOpen, openSearch, closeSearch } = useSearch();
  const [notificationPermission, setNotificationPermission] = useState(getNotificationPermission());
  const [privacyMode, setPrivacyModeState] = useState(getNotificationPrivacyMode());
  const [notificationsEnabled, setNotificationsEnabledState] = useState(getNotificationsEnabled());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotificationPermission(result);
  };

  const togglePrivacyMode = () => {
    const newMode = !privacyMode;
    setNotificationPrivacyMode(newMode);
    setPrivacyModeState(newMode);
  };

  const toggleNotifications = () => {
    const newEnabled = !notificationsEnabled;
    setNotificationsEnabled(newEnabled);
    setNotificationsEnabledState(newEnabled);
  };

  return (
    <header className="sticky top-0 z-40 h-[calc(4rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80 lg:static lg:z-50 lg:h-16 lg:pt-0 lg:bg-surface flex items-center justify-between px-4">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="p-2 text-muted hover:text-text lg:hidden"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Search button */}
        <button
          onClick={openSearch}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-light hover:bg-surface-light/80 transition-colors text-muted hover:text-text"
          title="Search (⌘K)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <span className="text-sm hidden sm:inline">Search</span>
          <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-surface rounded border border-border">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>

        <NotificationBell />
        
        <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-light transition-colors"
        >
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="w-8 h-8 rounded-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <span className="text-sm font-medium text-text hidden sm:block">{user?.name}</span>
          <svg
            className={`w-4 h-4 text-muted transition-transform ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDropdown && (
          <div className="fixed inset-x-2 top-14 sm:absolute sm:right-0 sm:left-auto sm:inset-x-0 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-48 bg-surface border border-border rounded-lg shadow-xl py-1 z-50">
            <div className="px-4 py-2 border-b border-border">
              <p className="text-sm font-medium text-text">{user?.name}</p>
              <p className="text-xs text-muted">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded">
                {user?.role}
              </span>
            </div>
            
            {/* Notification permission status */}
            {notificationPermission === 'default' && (
              <button
                onClick={handleEnableNotifications}
                className="w-full text-left px-4 py-2 text-sm text-text hover:bg-surface-light transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Enable Notifications
              </button>
            )}
            
            {notificationPermission === 'granted' && (
              <>
                {/* Enable/Disable notifications toggle */}
                <button
                  onClick={toggleNotifications}
                  className="w-full text-left px-4 py-2 text-sm text-text hover:bg-surface-light transition-colors flex items-center justify-between border-b border-border"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span>Desktop Notifications</span>
                  </div>
                  <div className={`w-9 h-5 rounded-full transition-colors ${notificationsEnabled ? 'bg-primary' : 'bg-surface-light'} relative`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${notificationsEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                  </div>
                </button>
                
                {/* Privacy mode toggle - only show if notifications are enabled */}
                {notificationsEnabled && (
                  <>
                    <button
                      onClick={togglePrivacyMode}
                      className="w-full text-left px-4 py-2 text-sm text-text hover:bg-surface-light transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span>Privacy Mode</span>
                      </div>
                      <div className={`w-9 h-5 rounded-full transition-colors ${privacyMode ? 'bg-primary' : 'bg-surface-light'} relative`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${privacyMode ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                    </button>
                    {privacyMode && (
                      <div className="px-4 py-1.5 text-[10px] text-muted leading-relaxed">
                        Message content hidden in notifications
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            
            {notificationPermission === 'denied' && (
              <div className="px-4 py-2 text-xs text-error flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                Notifications blocked
              </div>
            )}
            
            {/* Theme selector */}
            <div className="px-4 py-2 border-t border-border">
              <p className="text-xs font-medium text-muted mb-2 uppercase tracking-wider">Theme</p>
              <div className="space-y-1">
                <button
                  onClick={() => { setTheme('default'); updateThemePreference('default').then(() => refreshUser()).catch(() => {}); }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-3 ${
                    theme === 'default' ? 'bg-primary/20 text-primary' : 'text-text hover:bg-surface-light'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border-2 border-current flex-shrink-0" style={{ backgroundColor: '#22C55E' }} />
                  Default
                </button>
                <button
                  onClick={() => { setTheme('amber'); updateThemePreference('amber').then(() => refreshUser()).catch(() => {}); }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-3 ${
                    theme === 'amber' ? 'bg-primary/20 text-primary' : 'text-text hover:bg-surface-light'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border-2 border-current flex-shrink-0" style={{ backgroundColor: '#F59E0B' }} />
                  Amber
                </button>
                <button
                  onClick={() => { setTheme('indigo'); updateThemePreference('indigo').then(() => refreshUser()).catch(() => {}); }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-3 ${
                    theme === 'indigo' ? 'bg-primary/20 text-primary' : 'text-text hover:bg-surface-light'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border-2 border-current flex-shrink-0" style={{ backgroundColor: '#6366F1' }} />
                  Indigo
                </button>
                <button
                  onClick={() => { setTheme('purple'); updateThemePreference('purple').then(() => refreshUser()).catch(() => {}); }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-3 ${
                    theme === 'purple' ? 'bg-primary/20 text-primary' : 'text-text hover:bg-surface-light'
                  }`}
                >
                  <span className="w-4 h-4 rounded-full border-2 border-current flex-shrink-0" style={{ backgroundColor: '#A855F7' }} />
                  Dark Purple
                </button>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-error hover:bg-surface-light transition-colors border-t border-border"
            >
              Log out
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={closeSearch} />
    </header>
  );
}
